import React, {useMemo, useEffect} from 'react';
import {createBrowserRouter, Navigate, Outlet} from 'react-router-dom';
import Dashboard from '@/modules/patient-dashboard/DashboardView';
import LoginPage from '@/modules/auth/components/LoginPage';
import ResetPasswordPage from '@/modules/auth/components/ResetPasswordPage';
import Results from '@/modules/results/views/ResultsPage';
import AllBiomarkersResults from '@/modules/results/views/AllBiomarkersPage';
import TestKits from '@/modules/kits/views/TestKits';
import RegisterKit from '@/modules/kits/views/RegisterKit';
import IncentivesPage from '@/modules/incentives/views/IncentivesPage';
import Support from '@/shared/Support';
import Admin from '@/modules/admin/AdminView';
import AdminBioMatchFulfillmentView from '@/modules/biomatch/views/AdminBioMatchFulfillmentView';
import ManageSubscriptionsView from '@/modules/biomatch/views/ManageSubscriptionsView';
import AdminOrdersView from '@/modules/admin/views/AdminOrdersView';
import AdminOrderDetailView from '@/modules/admin/views/AdminOrderDetailView';
import AdminSubscriptionsView from '@/modules/admin/views/AdminSubscriptionsView';
import AdminMembershipsView from '@/modules/admin/views/AdminMembershipsView';
import MembershipBenefitsView from '@/modules/patient-dashboard/views/MembershipBenefitsView';

import PixelTestHarness from '@/modules/pixel-test/PixelTestHarness';
import PatientSwitcher from '@/modules/patient-dashboard/PatientSwitcher';
import Settings from '@/modules/settings/SettingsView';
import SingleResult from '@/modules/results/views/SingleResultPage';
import Verify from '@/modules/auth/views/Verify';
import ProviderConsole from '@/modules/provider-console/ProviderConsoleView';
import ProviderPatientResults from '@/modules/results/views/ProviderPatientResults';
import ProviderOrderTests from '@/modules/order-tests/views/ProviderOrderTests';
import ProviderOrderBuilder from '@/modules/order-tests/views/ProviderOrderBuilder';
import Launch from '@/modules/auth/views/Launch';
import OhdLaunch from '@/modules/auth/views/OhdLaunch';
import ShopLayout from '@/modules/shop/views/ShopLayout';
import ShopPage from '@/modules/shop/views/ShopPage';
import CartPage from '@/modules/shop/views/CartPage';
import CheckoutPage from '@/modules/shop/views/CheckoutPage';
import CartDrawer from '@/modules/shop/components/CartDrawer';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {decodeJwtToken, isOrganizationUser, isProviderOrOrganizationUser, isUserAdmin} from '@/shared/utils/tokenUtils';
import {useTenant} from '@/shared/hooks/useTenant';
import {GOODHUE_OHD_ORG_ID, readOhdOrgId} from '@/shared/constants/ohd';
import {ADMIN_RETURN_STATE_KEY} from '@/shared/constants/providerSwitch';
import RegisterPage from '@/modules/auth/components/RegisterPage';

function getDefaultAuthedRoute(token?: string | null): string {
    if (isUserAdmin(token ?? undefined)) {
        return '/admin';
    }
    if (isOrganizationUser(token)) return '/admin';
    return isProviderOrOrganizationUser(token) ? '/provider' : '/dashboard';
}

const buildAdminReturnTarget = (): string => {
    if (typeof window === 'undefined') return '/admin';
    try {
        const raw = sessionStorage.getItem(ADMIN_RETURN_STATE_KEY);
        if (!raw) return '/admin';
        const state = JSON.parse(raw) as {
            orgId?: number | string;
            orgDetailTab?: string;
            orgFilter?: string;
            providerFilter?: string;
        };
        const params = new URLSearchParams();
        if (state?.orgId != null) params.set('orgId', String(state.orgId));
        if (state?.orgDetailTab) params.set('tab', String(state.orgDetailTab));
        if (state?.orgFilter != null) params.set('orgFilter', String(state.orgFilter));
        if (state?.providerFilter != null) params.set('providerFilter', String(state.providerFilter));
        const query = params.toString();
        return query ? `/admin?${query}` : '/admin';
    } catch {
        return '/admin';
    }
};

/**
 * Detects when the JWT's org_subdomain doesn't match the tenant the browser
 * resolved.  When a mismatch is found the session is stale (e.g. user
 * navigated to a different subdomain after a provider-switch) so we force
 * a logout and return true so callers can redirect to /login.
 */
function useOrgMismatchGuard(): boolean {
    const {user, logout} = useAuth();
    const {organization} = useTenant();

    const token = user?.token;
    const isLoggedIn = user?.isLoggedIn ?? false;
    const orgSubdomain = organization?.subdomain;

    const mismatch = useMemo(() => {
        if (!token || !isLoggedIn || !orgSubdomain) return false;
        const payload = decodeJwtToken(token);
        const tokenSub = typeof payload?.org_subdomain === 'string' ? payload.org_subdomain : null;
        if (!tokenSub) return false;
        return tokenSub !== orgSubdomain;
    }, [token, isLoggedIn, orgSubdomain]);

    useEffect(() => {
        if (mismatch) {
            logout();
        }
    }, [mismatch, logout]);

    return mismatch;
}

/**
 * Top-level guard for all authenticated routes.
 * Redirects to /login if unauthenticated or if the session belongs to a
 * different org than the current subdomain (detected by useOrgMismatchGuard).
 */
const ProtectedLayout: React.FC = () => {
    const {user, isInitialized} = useAuth();
    const orgMismatch = useOrgMismatchGuard();

    if (!isInitialized) {
        return null;
    }

    if (!user?.isLoggedIn || orgMismatch) {
        return <Navigate to="/login" replace />;
    }

    return <Outlet />;
};

/**
 * Renders children only when the current tenant is GoodHue (OHD org 959).
 * Other tenants are redirected to /dashboard so the incentives URL behaves
 * like a non-existent page elsewhere.
 */
const IncentivesGate: React.FC = () => {
    const {organization, loading} = useTenant();

    if (loading) {
        return null;
    }

    if (readOhdOrgId(organization?.features) !== GOODHUE_OHD_ORG_ID) {
        return <Navigate to="/dashboard" replace />;
    }

    return <IncentivesPage />;
};

/** Restricts child routes to patient-role users; redirects admins and providers. */
const PatientOnlyLayout: React.FC = () => {
    const {user, isInitialized} = useAuth();

    if (!isInitialized) {
        return null;
    }

    if (!user?.isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    if (isUserAdmin(user.token)) {
        return <Navigate to="/admin" replace />;
    }

    if (isOrganizationUser(user.token)) {
        return <Navigate to="/admin" replace />;
    }

    if (isProviderOrOrganizationUser(user.token)) {
        return <Navigate to="/provider" replace />;
    }

    // CartDrawer is mounted here so patients can add a Custom Lab
    // Package or curated program from the dashboard hero and have the
    // drawer slide out — same UX as the /shop/* routes.
    return (
        <>
            <Outlet />
            <CartDrawer />
        </>
    );
};

/**
 * Layout for unauthenticated routes (/login, /register, /reset).
 * Redirects already-authenticated users to their default route — UNLESS
 * an org mismatch is detected, in which case the stale session is being
 * cleared and the user should stay on the login page.
 */
const AuthLayout: React.FC = () => {
    const {user, isInitialized} = useAuth();
    const orgMismatch = useOrgMismatchGuard();

    if (!isInitialized) {
        return null;
    }

    if (user?.isLoggedIn && !orgMismatch) {
        return <Navigate to={getDefaultAuthedRoute(user.token)} replace />;
    }

    return <Outlet />;
};

/** Catch-all redirect: sends authed users to their default route, others to /login. */
const DefaultRedirect: React.FC = () => {
    const {user, isInitialized} = useAuth();
    const orgMismatch = useOrgMismatchGuard();

    if (!isInitialized) {
        return null;
    }

    return <Navigate to={user?.isLoggedIn && !orgMismatch ? getDefaultAuthedRoute(user.token) : '/login'} replace />;
};

/**
 * Restricts child routes to provider/org-role users.
 * Admins are redirected to /admin (restoring any saved return state).
 * Patient-role users are redirected to /dashboard.
 */
const ProviderOnlyLayout: React.FC = () => {
    const {user, isInitialized} = useAuth();

    if (!isInitialized) {
        return null;
    }

    if (!user?.isLoggedIn) {
        return <Navigate to="/login" replace />;
    }

    if (isUserAdmin(user.token)) {
        return <Navigate to={buildAdminReturnTarget()} replace />;
    }

    if (isOrganizationUser(user.token)) {
        return <Navigate to="/admin" replace />;
    }

    // Match the app's "page doesn't exist" behavior.
    if (!isProviderOrOrganizationUser(user.token)) {
        return <Navigate to="/dashboard" replace />;
    }

    return <Outlet />;
};

export const router = createBrowserRouter([
    {
        // DEV-ONLY pixel-perfect comparison surface. No auth/layout — the
        // harness supplies its own mock providers. Guarded by import.meta.env.DEV
        // inside the component so a prod build renders only a notice.
        path: '/__pixel-test/:section',
        element: <PixelTestHarness />,
    },
    {
        element: <AuthLayout />,
        children: [
            {
                path: '/login',
                element: <LoginPage />,
            },
            {
                path: '/register',
                element: <RegisterPage />,
            },
            {
                path: '/reset',
                element: <ResetPasswordPage />,
            },
            {
                path: '/reset/:token',
                element: <ResetPasswordPage />,
            },
        ],
    },
    {
        element: <ProtectedLayout />,
        children: [
            {
                element: <PatientOnlyLayout />,
                children: [
                    {
                        path: '/dashboard',
                        element: <Dashboard />,
                    },
                    {
                        path: '/results',
                        element: <Results />,
                    },
                    {
                        path: '/results/biomarkers',
                        element: <AllBiomarkersResults />,
                    },
                    {
                        path: '/test-kits',
                        element: <TestKits />,
                    },
                    {
                        path: '/incentives',
                        element: <IncentivesGate />,
                    },
                    {
                        path: '/test-kits/activate',
                        element: <RegisterKit />,
                    },
                    {
                        path: '/patients',
                        element: <PatientSwitcher />,
                    },
                    {
                        path: '/settings',
                        element: <Settings />,
                    },
                    {
                        path: '/subscriptions',
                        element: <ManageSubscriptionsView />,
                    },
                    {
                        path: '/membership/benefits',
                        element: <MembershipBenefitsView />,
                    },
                ],
            },
            {
                path: '/result/:id',
                element: <SingleResult />,
            },
            {
                path: '/support',
                element: <Support />,
            },
            {
                path: '/admin',
                element: <Admin />,
            },
            {
                path: '/admin/biomatch-fulfillment',
                element: <AdminBioMatchFulfillmentView />,
            },
            {
                path: '/admin/orders',
                element: <AdminOrdersView />,
            },
            {
                path: '/admin/orders/:id',
                element: <AdminOrderDetailView />,
            },
            {
                path: '/admin/subscriptions',
                element: <AdminSubscriptionsView />,
            },
            {
                path: '/admin/memberships',
                element: <AdminMembershipsView />,
            },
            {
                element: <ProviderOnlyLayout />,
                children: [
                    {
                        path: '/provider',
                        element: <ProviderConsole />,
                    },
                    {
                        path: '/provider/patient/:userId',
                        element: <ProviderPatientResults />,
                    },
                    {
                        path: '/provider/order-tests',
                        element: <ProviderOrderTests />,
                    },
                    {
                        path: '/provider/order-tests/new/:userId',
                        element: <ProviderOrderBuilder />,
                    },
                ],
            },
        ],
    },
    {
        element: <ShopLayout />,
        children: [
            {
                path: '/shop',
                element: <ShopPage />,
            },
            {
                path: '/shop/cart',
                element: <CartPage />,
            },
            {
                path: '/shop/checkout',
                element: <CheckoutPage />,
            },
        ],
    },
    {
        path: '/verify/:token',
        element: <Verify />,
    },
    {
        path: '/launch',
        element: <Launch />,
    },
    {
        path: '/ohd-launch',
        element: <OhdLaunch />,
    },
    {
        path: '/',
        element: <DefaultRedirect />,
    },
    {
        path: '*',
        element: <DefaultRedirect />,
    },
]);
